import React, { useEffect } from 'react';
import { Dispatch } from '@reduxjs/toolkit';
import styled from '@emotion/styled';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Text, TextVariant } from '@naf/text';
import { breakpoints, spacing } from '@naf/theme';
import { Button } from '@naf-simba/elements-buttons';
import { ArrowForward } from '@naf-simba/building-blocks-icons/Arrows';
import { BilguideSearchBlockType } from '@appTypes/bilguideSearchBlockType';
import CarSearch from '@components/bilguide/carSearch/CarSearch';
import { actions as carFilterActions } from '@redux/modules/carFilter';
import { actions as carsActions } from '@redux/modules/cars';
import useSelector from '@redux/typedHooks';
import { UrlParamsType } from '@appTypes/urlParamsType';
import { createElbilguideAPIQuery } from '@utils/bilguide';
import { getURLParams } from '@lib/getUrlParams';

type BilguideSearchBlockProps = { block: BilguideSearchBlockType };

const fetchElectricCars = (dispatch: Dispatch, searchParams: UrlParamsType = {}) => {
  const queries = createElbilguideAPIQuery(searchParams);
  dispatch(carsActions.getCars.request(queries));
  dispatch(carFilterActions.setDidRefreshSearch());
};

const BilguideSearchBlock = ({ block }: BilguideSearchBlockProps) => {
  // TODO: Use this once we have some way to filter search on categories from Sanity
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { showCategories } = block;
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cars = useSelector((state) => state.cars);
  const { data: carSlugsByPage } = cars;
  const { filterNumber, filterFetched } = useSelector((state) => state.carFilter);
  const hasSingleResult = carSlugsByPage?.length === 1 && carSlugsByPage[0].length === 1;

  // Handle search submission (Enter key or button click)
  const handleSearchNavigation = (query?: string) => {
    const searchQuery = query || getURLParams().query || '';
    if (hasSingleResult) {
      navigate(`/bilguiden/bilmodell/${carSlugsByPage[0][0]}`);
    } else {
      navigate(`/bilguiden?query=${searchQuery}`);
    }
  };

  // Update selection when filter selections change in url
  useEffect(() => {
    if (filterNumber !== 1 && !filterFetched) {
      const parsedParams = getURLParams();
      fetchElectricCars(dispatch, parsedParams);
    }
  }, [filterNumber, filterFetched, dispatch]);

  // Update selection when no data is available
  useEffect(() => {
    if (
      typeof carSlugsByPage === 'undefined' ||
      carSlugsByPage.length === 0 ||
      (carSlugsByPage[0].length === 0 && !filterFetched)
    ) {
      const parsedParams = getURLParams();
      fetchElectricCars(dispatch, parsedParams);
    }
  }, [carSlugsByPage, dispatch, filterFetched, filterNumber]);

  return (
    <BilguideSearchBlockWrapper>
      <LabelText variant={TextVariant.DataLabel}>Søk etter modell</LabelText>
      <InputWrapper>
        <CarSearch onSearchSubmit={handleSearchNavigation} />
        <Button
          type="button"
          size="medium"
          variant="contained"
          color="secondary"
          shade="light"
          endIcon={ArrowForward}
          onClick={() => handleSearchNavigation()}
        >
          Søk
        </Button>
        <Button
          type="button"
          size="medium"
          variant="contained"
          color="secondary"
          shade="light"
          endIcon={ArrowForward}
          onClick={() => fetchElectricCars(dispatch)}
          to="/bilguiden"
        >
          Alle modeller
        </Button>
      </InputWrapper>
    </BilguideSearchBlockWrapper>
  );
};

const LabelText = styled(Text)`
  margin: 0;
`;

const BilguideSearchBlockWrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  row-gap: ${spacing.space8};
`;

const InputWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: ${spacing.space16} ${spacing.space8};

  /* The actual search field */
  form {
    flex-grow: 1;
    flex-shrink: 3;

    @media (min-width: calc(${breakpoints.m} + 1px)) {
      height: 56px;

      & > div:first-child {
        height: 56px;
      }

      & > div:nth-child(2) {
        top: 64px;
      }
    }
  }

  @media (max-width: ${breakpoints.m}) {
    flex-flow: row wrap;
  }
`;

export { BilguideSearchBlock, BilguideSearchBlockWrapper };
